import {inject, observer} from 'mobx-react';
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {useIntl} from 'react-intl';
import {useToasts} from 'react-toast-notifications';
import styled from 'styled-components';
import {ModalContext} from '../../apiContexts';
import {getEventsList} from '../../helpers/events';
import {
    getMemorizeReportForm,
    memorizeReportForm,
} from '../../helpers/report';
import history from '../../routes/history';
import {ReactSelectStyled} from '../../styles';
import EventConfig from '../../views/modals/newReport/eventConfig';
import RouteConfig from '../../views/modals/newReport/routeConfig';
import StopConfig from '../../views/modals/newReport/stopConfig';
import SummaryConfig from '../../views/modals/newReport/summaryConfig';
import TripConfig from '../../views/modals/newReport/tripConfig';
import JourneyConfig from '../../views/modals/newReport/journeyConfig';
import CertadocFineConfig from '../../views/modals/newReport/certadocFineConfig';
import useServices from '../../hooks/use-services';
import {useCertadocEnabled} from '../../autobahn/features/certadoc/queries';

const Styles = styled.div``;

const NewReportContainer = (props) => {
    const intl = useIntl();
    const {toggle} = useContext(ModalContext);
    const {groupService} = useServices();
    const [modalConfig, setModalConfig] = useState(null);
    const [groups, setGroups] = useState();
    const {addToast} = useToasts();
    const cacheReport = useMemo(() => getMemorizeReportForm('report'), []);
    const certadocEnabled = useCertadocEnabled();

    const reportOptions = useMemo(() => [
        {id: 1, title: intl.formatMessage({id: 'reportRoute'})},
        {id: 2, title: intl.formatMessage({id: 'reportEvents'})},
        {id: 3, title: intl.formatMessage({id: 'reportTrips'})},
        {id: 4, title: intl.formatMessage({id: 'reportStops'})},
        {id: 7, title: intl.formatMessage({id: 'reportJourney'})},
        {id: 5, title: intl.formatMessage({id: 'reportSummary'})},
        ...(certadocEnabled ? [{id: 8, title: intl.formatMessage({id: 'reportCertadocFines'})}] : []),
    ], [intl, certadocEnabled]);

    const devices = useMemo(
        () =>
            props.devices.list.map(({id, uniqueId, name}) => ({
                id,
                uniqueId,
                title: `${uniqueId} - ${name}`,
            })),
        [props.devices],
    );

    const events = useMemo(() => getEventsList(), []);

    useEffect(() => {
        (async () => {
            const groups = await groupService.list();
            setGroups(groups.map(({id, name}) => ({id, title: name})));
        })();

        cacheReport && cacheReport.report && setModalConfig(cacheReport.report.id);
        return () => {
            console.log('Unmount NewReportContainer');
        };
    }, []);

    const setReport = (value) => {
        if (value != null) {
            setModalConfig(value.id);
            memorizeReportForm({report: value});
        } else {
            setModalConfig(null);
            memorizeReportForm({report: null});
        }
    };

    const redirectToReport = (url) => {
        const {
            location: {pathname},
        } = history;

        const [type, search] = url.split('?');

        if (pathname.startsWith('/admin')) {
            url = `/admin/reports?${search}#${type}`;
            toggle();
            history.push(url);
        } else {
            url = `${pathname}?${search}#${type}`;
            toggle();
            history.push(url);
        }
    };

    const notifyToast = useCallback((appearance, error) => {
        let msg;
        if (appearance === 'success') {
            msg = (
                <span>
                    {intl.formatMessage({id: 'reportEmailSuccess'})}{' '}
                    <strong>{props.auth.email}</strong>.
                </span>
            );
        } else if (error && error.response && error.response.status === 503) {
            // O servidor responde 503 quando o envio de e-mail nem chegou a ser tentado.
            msg = intl.formatMessage({id: 'reportEmailNotConfigured'});
        } else {
            msg = intl.formatMessage({id: 'reportEmailError'});
        }
        addToast(msg, {appearance, autoDismiss: true});
    }, [intl]);

    return (
        <Styles>
            <ReactSelectStyled
                radius="9px"
                options={reportOptions}
                name="report"
                scoped={false}
                noOptionsMessage={() => intl.formatMessage({id: 'sharedNoResultFound'})}
                placeholder={intl.formatMessage({id: 'reportChooseReport'})}
                className="w-100 mb-3"
                onChange={setReport}
                defaultValue={cacheReport && cacheReport.report}
            />
            <div>
                {
                    {
                        1: (
                            <RouteConfig
                                {...{devices, groups, redirectToReport, notifyToast, toggle}}
                            />
                        ),
                        2: (
                            <EventConfig
                                {...{devices, groups, events, redirectToReport, notifyToast, toggle}}
                            />
                        ),
                        3: (
                            <TripConfig
                                {...{devices, groups, redirectToReport, notifyToast, toggle}}
                            />
                        ),
                        4: (
                            <StopConfig
                                {...{devices, groups, redirectToReport, notifyToast, toggle}}
                            />
                        ),
                        5: (
                            <SummaryConfig
                                {...{devices, groups, redirectToReport, notifyToast, toggle}}
                            />
                        ),
                        7: (
                            <JourneyConfig
                                {...{devices, groups, redirectToReport, notifyToast, toggle}}
                            />
                        ),
                        8: (
                            <CertadocFineConfig
                                {...{devices, groups, redirectToReport, notifyToast}}
                            />
                        ),
                    }[modalConfig]
                }
            </div>
        </Styles>
    );
};

const mapping = ({store}) => ({
    devices: store.devices,
    auth: store.auth,
});

export default inject(mapping)(observer(NewReportContainer));
